import { useState, useMemo, useRef, useCallback, useEffect } from "react";
import {
  PaperAirplaneIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  StopIcon,
} from "@heroicons/react/24/outline";
import { SignalIcon } from "@heroicons/react/24/solid";
import useMqttStore from "../../store/useMqttStore";
import { topicPrefix } from "../../constant";
import { ParameterData } from "../../types/device";

interface MqttPublishWidgetProps {
  deviceId: string;
  parameters: ParameterData[];
  dataRateSeconds?: number;
}

const DURATION_OPTIONS = [
  { label: "1 min", seconds: 60 },
  { label: "5 min", seconds: 300 },
  { label: "10 min", seconds: 600 },
  { label: "30 min", seconds: 1800 },
  { label: "1 hour", seconds: 3600 },
];

function buildRandomPayload(parameters: ParameterData[]) {
  const payload: Record<string, string | number | boolean> = {};
  parameters.forEach((param) => {
    if (param.dataType === "BOOLEAN") {
      payload[param.identifier] = Math.random() > 0.5;
    } else if (param.dataType === "STRING") {
      payload[param.identifier] = "test_value";
    } else {
      const min = param.minValue ?? 0;
      const max = param.maxValue ?? 100;
      const val = min + Math.random() * (max - min);
      payload[param.identifier] =
        param.dataType === "INTEGER"
          ? Math.round(val)
          : parseFloat(val.toFixed(2));
    }
  });
  return payload;
}

function formatTimeRemaining(totalSeconds: number) {
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s.toString().padStart(2, "0")}s`;
}

export default function MqttPublishWidget({
  deviceId,
  parameters,
  dataRateSeconds,
}: MqttPublishWidgetProps) {
  const { publishMessage, isPublishConnected, reconnectPublishClient } =
    useMqttStore();
  const isConnected = isPublishConnected;

  // Establish the dedicated publish (device) connection when the widget mounts
  // so the status badge reflects reality and publishing works immediately.
  useEffect(() => {
    if (!isPublishConnected) reconnectPublishClient();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [values, setValues] = useState<Record<string, string>>({});
  const [publishStatus, setPublishStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  // Continuous publish state
  const [selectedDuration, setSelectedDuration] = useState(300); // default 5 min
  const [isStreaming, setIsStreaming] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [messagesSent, setMessagesSent] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const endTimeRef = useRef<number>(0);

  const topic = `${topicPrefix}/${deviceId}`;
  const publishInterval = 10;

  const stopStreaming = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    intervalRef.current = null;
    countdownRef.current = null;
    endTimeRef.current = 0;
    setIsStreaming(false);
    setTimeRemaining(0);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, []);

  const startStreaming = useCallback(() => {
    if (!isConnected) return;
    stopStreaming();

    const endTime = Date.now() + selectedDuration * 1000;
    endTimeRef.current = endTime;
    setIsStreaming(true);
    setMessagesSent(0);
    setTimeRemaining(selectedDuration);

    // Send first message immediately
    const payload = buildRandomPayload(parameters);
    publishMessage(topic, payload);
    setMessagesSent(1);

    // Update the displayed values
    const display: Record<string, string> = {};
    Object.entries(payload).forEach(([k, v]) => {
      display[k] = String(v);
    });
    setValues(display);

    // Set up periodic publishing
    intervalRef.current = setInterval(() => {
      if (Date.now() >= endTimeRef.current) {
        stopStreaming();
        return;
      }
      const p = buildRandomPayload(parameters);
      publishMessage(topic, p);
      setMessagesSent((prev) => prev + 1);
      const d: Record<string, string> = {};
      Object.entries(p).forEach(([k, v]) => {
        d[k] = String(v);
      });
      setValues(d);
    }, publishInterval * 1000);

    // Set up countdown timer (updates every second)
    countdownRef.current = setInterval(() => {
      const remaining = Math.max(
        0,
        Math.ceil((endTimeRef.current - Date.now()) / 1000)
      );
      setTimeRemaining(remaining);
      if (remaining <= 0) {
        stopStreaming();
      }
    }, 1000);
  }, [
    isConnected,
    selectedDuration,
    parameters,
    topic,
    publishMessage,
    publishInterval,
    stopStreaming,
  ]);

  const updateValue = (identifier: string, value: string) => {
    setValues((prev) => ({ ...prev, [identifier]: value }));
    setPublishStatus("idle");
  };

  const generateRandomValues = () => {
    const randomValues: Record<string, string> = {};
    parameters.forEach((param) => {
      if (param.dataType === "BOOLEAN") {
        randomValues[param.identifier] =
          Math.random() > 0.5 ? "true" : "false";
      } else if (param.dataType === "STRING") {
        randomValues[param.identifier] = "test_value";
      } else {
        const min = param.minValue ?? 0;
        const max = param.maxValue ?? 100;
        const val = min + Math.random() * (max - min);
        randomValues[param.identifier] =
          param.dataType === "INTEGER"
            ? String(Math.round(val))
            : String(parseFloat(val.toFixed(2)));
      }
    });
    setValues(randomValues);
    setPublishStatus("idle");
  };

  const payload = useMemo(() => {
    const data: Record<string, string | number | boolean> = {};
    parameters.forEach((param) => {
      const raw = values[param.identifier];
      if (raw === undefined || raw === "") return;
      if (param.dataType === "BOOLEAN") {
        data[param.identifier] = raw === "true";
      } else if (param.dataType === "STRING") {
        data[param.identifier] = raw;
      } else if (param.dataType === "FLOAT") {
        data[param.identifier] = parseFloat(raw);
      } else {
        data[param.identifier] = parseInt(raw, 10);
      }
    });
    return data;
  }, [values, parameters]);

  const filledCount = Object.keys(payload).length;

  const handlePublish = () => {
    if (!isConnected || filledCount === 0) return;
    const success = publishMessage(topic, payload);
    setPublishStatus(success ? "success" : "error");
    if (success) {
      setTimeout(() => setPublishStatus("idle"), 2000);
    }
  };

  const handlePublishRandom = () => {
    if (!isConnected) return;
    const randomPayload = buildRandomPayload(parameters);
    const success = publishMessage(topic, randomPayload);
    setPublishStatus(success ? "success" : "error");
    if (success) {
      const display: Record<string, string> = {};
      Object.entries(randomPayload).forEach(([k, v]) => {
        display[k] = String(v);
      });
      setValues(display);
      setTimeout(() => setPublishStatus("idle"), 2000);
    }
  };

  if (parameters.length === 0) return null;

  return (
    <div className="bg-white dark:bg-[#111B21] rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-slate-50 to-gray-50 dark:from-gray-800 dark:to-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                MQTT Publish
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Send test values to device topic
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isConnected ? (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800">
                <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                <span>Broker Connected</span>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                  <div className="w-2 h-2 rounded-full bg-red-500" />
                  <span>Disconnected</span>
                </div>
                <button
                  onClick={() => reconnectPublishClient()}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-amber-600 rounded-full hover:bg-amber-700 transition-colors"
                >
                  <ArrowPathIcon className="h-3.5 w-3.5" />
                  Reconnect
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Topic Display */}
      <div className="px-6 pt-4">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span className="font-medium">Topic:</span>
          <code className="bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded text-gray-700 dark:text-gray-300 font-mono">
            {topic}
          </code>
        </div>
      </div>

      {/* Parameter Inputs */}
      <div className="px-6 py-4">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
          {parameters.map((param) => (
            <div key={param.identifier} className="space-y-1.5">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
                <span>{param.name}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500 font-normal">
                  {param.unit}
                </span>
              </label>
              {param.dataType === "BOOLEAN" ? (
                <select
                  value={values[param.identifier] ?? ""}
                  onChange={(e) =>
                    updateValue(param.identifier, e.target.value)
                  }
                  disabled={isStreaming}
                  className="w-full h-10 px-3 py-1.5 border-0 rounded-lg bg-white ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:outline-none focus:ring-2 focus:ring-primary dark:bg-gray-700 dark:text-gray-200 text-gray-900 text-sm sm:leading-6 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <option value="">--</option>
                  <option value="true">true</option>
                  <option value="false">false</option>
                </select>
              ) : (
                <input
                  type={param.dataType === "STRING" ? "text" : "number"}
                  step={param.dataType === "FLOAT" ? "0.01" : "1"}
                  placeholder={
                    param.minValue != null && param.maxValue != null
                      ? `${param.minValue} - ${param.maxValue}`
                      : `Enter ${param.dataType.toLowerCase()}`
                  }
                  value={values[param.identifier] ?? ""}
                  onChange={(e) =>
                    updateValue(param.identifier, e.target.value)
                  }
                  disabled={isStreaming}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:text-white font-mono disabled:opacity-50"
                />
              )}
              <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                <code className="font-mono">{param.identifier}</code>
                <span className="uppercase">{param.dataType}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Payload Preview */}
      {filledCount > 0 && (
        <div className="px-6 pb-3">
          <details className="group">
            <summary className="text-xs font-medium text-gray-500 dark:text-gray-400 cursor-pointer hover:text-gray-700 dark:hover:text-gray-300">
              Preview payload ({filledCount} field
              {filledCount !== 1 ? "s" : ""})
            </summary>
            <pre className="mt-2 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg text-xs font-mono text-gray-700 dark:text-gray-300 overflow-auto max-h-32 border border-gray-200 dark:border-gray-700">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </details>
        </div>
      )}

      {/* Continuous Publish Section */}
      <div className="mx-6 mb-4 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-gray-50/50 dark:bg-gray-800/30 p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Continuous Random Publish
            </h4>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Sends random values every{" "}
              <span className="font-medium">10s</span>
            </p>
          </div>
          {isStreaming && (
            <div className="flex items-center gap-3">
              <div className="text-right">
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  {messagesSent} sent
                </div>
                <div className="text-sm font-mono font-semibold text-amber-600 dark:text-amber-400">
                  {formatTimeRemaining(timeRemaining)}
                </div>
              </div>
              <div className="h-8 w-8 rounded-full border-2 border-amber-500 border-t-transparent animate-spin" />
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {DURATION_OPTIONS.map((opt) => (
            <button
              key={opt.seconds}
              disabled={isStreaming}
              onClick={() => setSelectedDuration(opt.seconds)}
              className={`px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                selectedDuration === opt.seconds && !isStreaming
                  ? "bg-amber-100 dark:bg-amber-900/30 border-amber-400 dark:border-amber-600 text-amber-800 dark:text-amber-300"
                  : "bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-amber-300 dark:hover:border-amber-600"
              }`}
            >
              {opt.label}
            </button>
          ))}

          <div className="ml-auto flex items-center gap-2">
            {!isStreaming ? (
              <button
                onClick={startStreaming}
                disabled={!isConnected}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <PlayIcon className="h-4 w-4" />
                Start Streaming
              </button>
            ) : (
              <button
                onClick={stopStreaming}
                className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                <StopIcon className="h-4 w-4" />
                Stop
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Single Publish Actions */}
      <div className="bg-gray-50/50 dark:bg-gray-800/50 border-t border-gray-200 dark:border-gray-700 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {publishStatus === "success" && (
              <div className="flex items-center gap-1.5 text-green-600 dark:text-green-400 text-sm">
                <CheckCircleIcon className="h-4 w-4" />
                <span>Published</span>
              </div>
            )}
            {publishStatus === "error" && (
              <div className="flex items-center gap-1.5 text-red-600 dark:text-red-400 text-sm">
                <ExclamationTriangleIcon className="h-4 w-4" />
                <span>Failed to publish</span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={generateRandomValues}
              disabled={isStreaming}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowPathIcon className="h-3.5 w-3.5" />
              Fill Random
            </button>
            <button
              onClick={handlePublishRandom}
              disabled={!isConnected || isStreaming}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-3.5 w-3.5" />
              Send Random
            </button>
            <button
              onClick={handlePublish}
              disabled={!isConnected || filledCount === 0 || isStreaming}
              className="inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium text-white bg-amber-600 rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <PaperAirplaneIcon className="h-3.5 w-3.5" />
              Publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
